import React from 'react';
import { Select, MenuItem, Stack, Button, Typography, TextField, FormHelperText, FormControl, InputLabel } from '@mui/material';
import { commonMessages } from 'utils/commonMessages';
import { FormProvider, useForm } from 'react-hook-form';
import { Reason } from 'modules/service-call/types';
import { ServiceCallsApi } from 'modules/service-call/api';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { AttachmentsSection } from './AttachmentsSection';
import { ExplanationForm } from './ExplanationStep';

export type ExplanationStepFormData = {
  reasonId: number,
  attachments: { file: File }[]
  note: string
}

interface ExplanationStepActiveProps {
  reasonId?: number
  userNote?: string
  attachments: File[]
  completeStep(explanationForm: ExplanationForm): void
}

export const ExplanationStepActive: React.FC<ExplanationStepActiveProps> = ({ reasonId, userNote, attachments, completeStep }) => {
  const { formatMessage } = useIntl();
  const methods = useForm<ExplanationStepFormData>({
    defaultValues: {
      reasonId: reasonId,
      note: userNote,
      attachments: attachments.map(a => ({ file: a }))
    },
    mode: 'onChange'
  });
  const reasonsQuery = useQuery<Reason[]>(['reasons'], ServiceCallsApi.getReasons);

  const onSubmit = (formdata: ExplanationStepFormData) => {
    const files = formdata.attachments.map(attachment => attachment.file);
    completeStep({ ...formdata, attachments: files });
  };

  return (
    <FormProvider {...methods} >
      <Stack marginTop={5} gap={3}>
        <Stack>
          <FormControl fullWidth>
            <InputLabel error={Boolean(methods.formState.errors.reasonId)}>{formatMessage({ id: 'ExplanationStep.ReasonLabel', defaultMessage: 'Select reason' })}</InputLabel>
            <Select
              {...methods.register('reasonId', { required: formatMessage(commonMessages.fieldIsRequired) })}
              defaultValue={reasonId || ''}
              label={formatMessage({ id: 'ExplanationStep.ReasonLabel', defaultMessage: 'Select reason' })}
              error={Boolean(methods.formState.errors.reasonId)}
            >
              {reasonsQuery.isFetched && reasonsQuery.data?.map((reason) => (
                <MenuItem key={reason.id} value={reason.id}>{reason.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {methods.formState.errors.reasonId && (
            <FormHelperText error>{methods.formState.errors.reasonId?.message}</FormHelperText>
          )}
        </Stack>

        <AttachmentsSection />

        <Stack gap={2}>
          <Stack>
            <Typography variant='subtitle1' fontWeight={500}>{formatMessage({ id: 'UserNote.Title', defaultMessage: 'Note' })}</Typography>
            <Typography variant='body2' color='text.secondary'>{formatMessage({ id: 'UserNote.NotePrompt', defaultMessage: 'Write here any other information that may be relevant to the request' })}</Typography>
          </Stack>
          <TextField
            {...methods.register('note')}
            multiline
            minRows={5}
          />
        </Stack>

        <Stack direction='row-reverse'>
          <Button variant='contained' onClick={methods.handleSubmit(onSubmit)}>
            {formatMessage(commonMessages.continue)}
          </Button>
        </Stack>
      </Stack >
    </FormProvider>
  );
};